<template>
    <div>
        <card-header title="New Other Transaction" icon="fa-plus"/>

        <card-body>
            <card-list>
                <subheader-list-item icon="fa-info-circle" title="Transaction Details" />

                <container-list-item padded no-border>
                    <b-field label="Description" label-position="inside">
                        <b-input v-model="transaction.description" required></b-input>
                    </b-field>
                    <b-field label="Base Amount" label-position="inside">
                        <b-input v-model="transaction.baseAmount" type="number" step="0.01" required></b-input>
                    </b-field>
                </container-list-item>

                <subheader-list-item icon="fa-calculator" title="Tax Information" />

                <container-list-item padded no-border>
                    <b-field label="How would you like to enter tax information?">
                        <b-select v-model="taxMethod" expanded @input="resetTaxInfo">
                            <option value="user">Look up existing user</option>
                            <option value="address">Enter address to calculate</option>
                            <option value="manual">Enter tax information manually</option>
                        </b-select>
                    </b-field>
                </container-list-item>

                <!-- Option A: User Lookup -->
                <template v-if="taxMethod === 'user'">
                    <subheader-list-item icon="fa-user" title="User Lookup" />
                    <container-list-item padded no-border>
                        <b-field>
                            <b-autocomplete
                                :data="userQuery"
                                field="title"
                                :loading="isFetching"
                                @typing="searchUsers"
                                @select="selectUser"
                                v-if="!selectedUser"
                                icon="search"
                                placeholder="Search for user...">

                                <template slot-scope="props">
                                    <i class="fas fa-user mr-2"></i>{{ props.option.first_name }} {{ props.option.last_name }}<br>
                                    <small class="mr-4"><i class="far fa-envelope mr-2"></i>{{ props.option.email_masked }}</small>
                                    <small><i class="far fa-barcode-alt mr-2"></i>{{ props.option.id }}</small>
                                </template>

                                <template slot="empty" v-if="!isFetching">No results.</template>
                            </b-autocomplete>

                            <div v-else class="columns">
                                <div class="column is-10 pl-5">
                                    <i class="fas fa-user mr-2"></i><strong>{{ selectedUser.first_name }} {{ selectedUser.last_name }}</strong><br>
                                    <small class="mr-4"><i class="far fa-envelope mr-2"></i>{{ selectedUser.email }}</small>
                                    <small><i class="far fa-barcode-alt mr-2"></i>{{ selectedUser.id }}</small>
                                </div>
                                <div class="column has-text-right">
                                    <b-button @click="clearUser"><i class="far fa-fw fa-times"></i></b-button>
                                </div>
                            </div>
                        </b-field>
                    </container-list-item>

                    <template v-if="selectedUser">
                        <separator-list-item />
                        <container-list-item no-border>
                            <data-row title="Address">{{ selectedUser.address || 'N/A' }}</data-row>
                            <data-row title="City">{{ selectedUser.city || 'N/A' }}</data-row>
                            <data-row title="State">{{ selectedUser.state || 'N/A' }}</data-row>
                            <data-row title="ZIP">{{ selectedUser.zip || 'N/A' }}</data-row>
                        </container-list-item>
                    </template>
                </template>

                <!-- Option B: Address Entry -->
                <template v-if="taxMethod === 'address'">
                    <subheader-list-item icon="fa-map-marker-alt" title="Address for Tax Calculation" />
                    <container-list-item padded no-border>
                        <b-field label="Street Address" label-position="inside">
                            <b-input v-model="address.street"></b-input>
                        </b-field>
                        <b-field label="City" label-position="inside">
                            <b-input v-model="address.city"></b-input>
                        </b-field>
                        <div class="columns">
                            <div class="column is-6">
                                <b-field label="State" label-position="inside">
                                    <b-input v-model="address.state" maxlength="2"></b-input>
                                </b-field>
                            </div>
                            <div class="column is-6">
                                <b-field label="ZIP Code" label-position="inside">
                                    <b-input v-model="address.zip"></b-input>
                                </b-field>
                            </div>
                        </div>
                        <div class="mt-3">
                            <b-button type="is-info" @click="calculateTaxByAddress" :loading="calculatingTax" :disabled="!transaction.baseAmount">
                                <i class="fas fa-calculator mr-2"></i>Calculate Tax
                            </b-button>
                        </div>
                    </container-list-item>
                </template>

                <!-- Option C: Manual Tax Entry -->
                <template v-if="taxMethod === 'manual'">
                    <subheader-list-item icon="fa-edit" title="Manual Tax Information" />
                    <container-list-item padded no-border>
                        <b-field label="Tax Amount" label-position="inside">
                            <b-input v-model="manualTax.taxAmount" type="number" step="0.01"></b-input>
                        </b-field>
                        <div class="columns">
                            <div class="column">
                                <b-field label="State Rate" label-position="inside">
                                    <b-input v-model="manualTax.stateRate" type="number" step="0.001" placeholder="e.g., 0.065"></b-input>
                                </b-field>
                            </div>
                            <div class="column">
                                <b-field label="Local Rate" label-position="inside">
                                    <b-input v-model="manualTax.localRate" type="number" step="0.001" placeholder="e.g., 0.024"></b-input>
                                </b-field>
                            </div>
                        </div>
                        <b-field label="Jurisdiction" label-position="inside">
                            <b-input v-model="manualTax.jurisdiction" placeholder="e.g., SPOKANE CITY"></b-input>
                        </b-field>
                        <b-field label="Location Code" label-position="inside">
                            <b-input v-model="manualTax.locationCode" placeholder="e.g., 3210"></b-input>
                        </b-field>
                    </container-list-item>
                </template>

                <!-- Tax Results Display (for user/address methods) -->
                <template v-if="taxInfo && (taxMethod === 'user' || taxMethod === 'address')">
                    <subheader-list-item icon="fa-receipt" title="Tax Details" />
                    <container-list-item no-border>
                        <data-row title="Jurisdiction">{{ taxInfo.jurisdiction || 'N/A' }}</data-row>
                        <data-row title="Location Code">{{ taxInfo.locationCode || 'N/A' }}</data-row>
                        <data-row title="State Rate">{{ formatPercent(taxInfo.stateRate) }}</data-row>
                        <data-row title="Local Rate">{{ formatPercent(taxInfo.localRate) }}</data-row>
                        <data-row title="Total Rate">{{ formatPercent(taxInfo.taxRate) }}</data-row>
                    </container-list-item>
                    <container-list-item padded no-border>
                        <b-field label="Tax Amount" label-position="inside">
                            <b-input v-model="calculatedTaxAmount" type="number" step="0.01"></b-input>
                        </b-field>
                        <data-row title="Total Amount"><strong>{{ totalAmount | currency }}</strong></data-row>
                    </container-list-item>
                </template>

                <!-- Summary for manual entry -->
                <template v-if="taxMethod === 'manual'">
                    <subheader-list-item icon="fa-receipt" title="Summary" />
                    <container-list-item no-border>
                        <data-row title="Base Amount">{{ transaction.baseAmount | currency }}</data-row>
                        <data-row title="Tax Amount">{{ manualTax.taxAmount | currency }}</data-row>
                        <data-row title="Total Amount"><strong>{{ manualTotalAmount | currency }}</strong></data-row>
                    </container-list-item>
                </template>

                <!-- Advanced Fields -->
                <subheader-list-item icon="fa-cogs" title="Advanced" />
                <container-list-item padded no-border>
                    <b-field label="Completed Date" label-position="inside">
                        <b-datepicker
                            v-model="advanced.completedAt"
                            placeholder="Select date"
                            icon="calendar"
                            :max-date="new Date()"
                        />
                    </b-field>
                    <b-field label="Type ID" label-position="inside">
                        <b-input v-model="advanced.typeId" type="number" placeholder="Related entity ID"></b-input>
                    </b-field>
                    <b-field label="Intent ID" label-position="inside">
                        <b-input v-model="advanced.intentId" placeholder="e.g., pi_..."></b-input>
                    </b-field>
                    <b-field label="Charge ID" label-position="inside">
                        <b-input v-model="advanced.chargeId" placeholder="e.g., ch_..."></b-input>
                    </b-field>
                </container-list-item>
            </card-list>
        </card-body>

        <card-footer class="pt-3 pb-3">
            <div class="columns">
                <div class="column has-text-right">
                    <b-button
                        type="is-primary"
                        size="is-small"
                        @click="submit"
                        :disabled="!canSubmit"
                    ><i class="fas fa-plus mr-2"></i>Create Transaction
                    </b-button>
                </div>
            </div>
        </card-footer>
    </div>
</template>

<script>
    import CardHeader from "@/TIER/components/CardHeader";
    import CardBody from "@/TIER/components/CardBody";
    import CardFooter from "@/TIER/components/CardFooter";
    import CardList from "@/TIER/components/CardList";
    import SubheaderListItem from "@/TIER/components/SubheaderListItem";
    import SeparatorListItem from "@/TIER/components/SeparatorListItem";
    import ContainerListItem from "@/TIER/components/ContainerListItem";
    import DataRow from "@/TIER/components/DataRow";
    import debounce from "debounce";
    import moment from "moment";
    import {client as http} from "@/http_client";

    export default {
        components: {
            CardHeader, CardBody, CardFooter, CardList,
            SubheaderListItem, SeparatorListItem, ContainerListItem, DataRow
        },
        props: ['card', 'props'],
        data() {
            return {
                transaction: {
                    description: '',
                    baseAmount: null
                },
                taxMethod: 'user',

                // User lookup
                userQuery: [],
                isFetching: false,
                selectedUser: null,

                // Address entry
                address: {
                    street: '',
                    city: '',
                    state: 'WA',
                    zip: ''
                },

                // Manual tax entry
                manualTax: {
                    taxAmount: null,
                    stateRate: null,
                    localRate: null,
                    jurisdiction: '',
                    locationCode: ''
                },

                // Tax calculation results
                taxInfo: null,
                calculatingTax: false,
                calculatedTaxAmount: null,

                // Advanced fields
                advanced: {
                    completedAt: new Date(),
                    typeId: null,
                    intentId: '',
                    chargeId: ''
                }
            };
        },
        computed: {
            totalAmount() {
                const base = parseFloat(this.transaction.baseAmount) || 0;
                const tax = parseFloat(this.calculatedTaxAmount) || 0;
                return base + tax;
            },
            manualTotalAmount() {
                const base = parseFloat(this.transaction.baseAmount) || 0;
                const tax = parseFloat(this.manualTax.taxAmount) || 0;
                return base + tax;
            },
            canSubmit() {
                if (!this.transaction.description || !this.transaction.baseAmount) return false;

                if (this.taxMethod === 'manual') {
                    return this.manualTax.taxAmount !== null && this.manualTax.taxAmount !== '';
                }

                return this.taxInfo !== null && this.calculatedTaxAmount !== null;
            }
        },
        watch: {
            'transaction.baseAmount': function(newVal) {
                // Recalculate tax when base amount changes if user is selected
                if (this.taxMethod === 'user' && this.selectedUser && newVal) {
                    this.calculateTaxForUser();
                }
            }
        },
        methods: {
            formatPercent(value) {
                if (value === null || value === undefined) return 'N/A';
                return (value * 100).toFixed(3) + '%';
            },
            resetTaxInfo() {
                this.taxInfo = null;
                this.calculatedTaxAmount = null;
            },
            searchUsers: debounce(function (name) {
                if (!name.length) {
                    this.userQuery = [];
                    return;
                }
                this.isFetching = true;
                http.get(`users/search/${name}/0/10`).then(response => {
                    this.userQuery = response.data.users;
                }).catch((error) => {
                    this.userQuery = [];
                    throw error;
                }).finally(() => {
                    this.isFetching = false;
                });
            }, 800),

            selectUser(option) {
                this.selectedUser = option;
                this.taxInfo = null;
                this.calculatedTaxAmount = null;
                // Auto-calculate tax when user is selected
                this.$nextTick(() => {
                    this.calculateTaxForUser();
                });
            },

            clearUser() {
                this.selectedUser = null;
                this.taxInfo = null;
                this.calculatedTaxAmount = null;
            },

            calculateTaxForUser() {
                if (!this.selectedUser || !this.transaction.baseAmount) return;

                this.calculatingTax = true;
                http.post('sales-tax/calculate-for-user', {
                    userId: this.selectedUser.id,
                    baseAmount: parseFloat(this.transaction.baseAmount)
                }).then(response => {
                    this.taxInfo = response.data;
                    this.calculatedTaxAmount = response.data.taxAmount;
                }).catch(err => {
                    window.alert('Error calculating tax: ' + (err.response?.data?.error || err.message));
                }).finally(() => {
                    this.calculatingTax = false;
                });
            },

            calculateTaxByAddress() {
                if (!this.transaction.baseAmount) return;

                this.calculatingTax = true;
                http.post('sales-tax/calculate-by-address', {
                    address: this.address.street,
                    city: this.address.city,
                    state: this.address.state,
                    zip: this.address.zip,
                    baseAmount: parseFloat(this.transaction.baseAmount)
                }).then(response => {
                    this.taxInfo = response.data;
                    this.calculatedTaxAmount = response.data.taxAmount;
                }).catch(err => {
                    window.alert('Error calculating tax: ' + (err.response?.data?.error || err.message));
                }).finally(() => {
                    this.calculatingTax = false;
                });
            },

            submit() {
                this.$emit('loading', true);

                let payload = {
                    description: this.transaction.description,
                    baseAmount: parseFloat(this.transaction.baseAmount)
                };

                if (this.taxMethod === 'manual') {
                    payload.taxAmount = parseFloat(this.manualTax.taxAmount) || 0;
                    payload.taxRate = (parseFloat(this.manualTax.stateRate) || 0) + (parseFloat(this.manualTax.localRate) || 0);
                    payload.taxStateRate = parseFloat(this.manualTax.stateRate) || 0;
                    payload.taxLocalRate = parseFloat(this.manualTax.localRate) || 0;
                    payload.taxJurisdiction = this.manualTax.jurisdiction;
                    payload.taxLocationCode = this.manualTax.locationCode;
                    payload.taxResultCode = 0;
                } else {
                    payload.taxAmount = parseFloat(this.calculatedTaxAmount) || 0;
                    payload.taxRate = this.taxInfo.taxRate;
                    payload.taxStateRate = this.taxInfo.stateRate;
                    payload.taxLocalRate = this.taxInfo.localRate;
                    payload.taxJurisdiction = this.taxInfo.jurisdiction;
                    payload.taxLocationCode = this.taxInfo.locationCode;
                    payload.taxResultCode = this.taxInfo.resultCode;
                    payload.userAddress = this.taxInfo.address;
                }

                if (this.taxMethod === 'user' && this.selectedUser) {
                    payload.userId = this.selectedUser.id;
                }

                // Advanced fields
                if (this.advanced.completedAt) {
                    payload.completedAt = moment(this.advanced.completedAt).toISOString();
                }
                if (this.advanced.typeId) {
                    payload.typeId = parseInt(this.advanced.typeId);
                }
                if (this.advanced.intentId) {
                    payload.intentId = this.advanced.intentId;
                }
                if (this.advanced.chargeId) {
                    payload.chargeId = this.advanced.chargeId;
                }

                http.post('sales-tax/other-transactions', payload, {force: true}).then(response => {
                    this.$reloadCard('sales-tax-menu');
                    this.$closeCard(this.card);
                }).catch(err => {
                    window.alert('An error occurred while saving the transaction.');
                    this.$emit('loading', false);
                });
            }
        },
        mounted() {
            this.$emit('loading', false);
        }
    };
</script>
